import { isSameDay, isSameMonth, isValidDate } from "@components/inputs/date/utils";
import { ISmartFieldChange, ISmartFieldValidationError } from "@components/smart/smartField/SmartField";
import {
    FiscalPeriodEntity,
    FiscalYearEntity,
    IChartOfAccountsEntity,
    IFiscalYearEntity
} from "@odata/GeneratedEntityTypes";
import { FiscalYearStatusCode } from "@odata/GeneratedEnums";
import React from "react";

import { Button, ButtonGroup } from "../../components/button";
import SmartFormDeleteButton from "../../components/smart/smartFormDeleteButton/SmartFormDeleteButton";
import { AppContext } from "../../contexts/appContext/AppContext.types";
import { withPermissionContext } from "../../contexts/permissionContext/withPermissionContext";
import { FormMode, Sort, ValidationErrorType } from "../../enums";
import BindingContext, { IEntity } from "../../odata/BindingContext";
import { getUtcDayjs } from "../../types/Date";
import { FormStorage, IFormStorageSaveResult, ISaveArgs } from "../../views/formView/FormStorage";
import { FormViewForExtend } from "../../views/formView/FormView";
import { createChartOfAccountsFromChartOfAccounts } from "../chartOfAccounts/ChartOfAccounts.utils";
import {
    createDefaultPeriods,
    DefaultPeriodsPath,
    FiscalYearDefaultPeriod,
    getActiveFiscalYears,
    getFiscalPeriodNumber,
    getFiscalYearNumber,
    getSortedFYs,
    IFiscalYearEntityExtendedEntity,
    refreshFiscalYears
} from "./FiscalYear.utils";


class FiscalYearFormView extends FormViewForExtend<IFiscalYearEntityExtendedEntity> {
    static contextType = AppContext;

    static defaultProps = {
        title: "FiscalYear:TitleSingular",
        formProps: {
            shouldHideVariant: true
        }
    };

    onAfterLoad = async () => {
        if (this.props.storage.formMode !== FormMode.AuditTrail) {
            if (this.props.storage.data.bindingContext.isNew()) {
                await this.setDefaultInterval();

                // default periods should be monthly by default
                this.setDefaultPeriods(FiscalYearDefaultPeriod.Monthly);
                this.createDefaultPeriods(FiscalYearDefaultPeriod.Monthly);
                this.props.storage.refreshFields();
            } else {
                const currentDataLook = this.getCurrentDataLook();

                this.setDefaultPeriods(currentDataLook);
            }

            this.revalidatePeriods(true);
        }

        this.props.storage.refresh();
        return super.onAfterLoad();
    };

    correctPeriodNumbers(storage: FormStorage<IFiscalYearEntity>) {
        const { entity, bindingContext } = storage.data;
        // correct possible wrong period numbers, so they match with FY
        entity.Periods?.forEach((period: IEntity, idx) => {
            const periodNumberBc = bindingContext.navigate(FiscalYearEntity.Periods).addKey(period).navigate(FiscalPeriodEntity.Number);
            storage.setValue(periodNumberBc, getFiscalPeriodNumber(entity.Number, idx));
        });
    }

    getFiscalYears = (): IFiscalYearEntity[] => {
        return getSortedFYs(this.context);
    };

    getOtherFiscalYears = (): IFiscalYearEntity[] => {
        return this.getFiscalYears().filter((fiscalYear: IFiscalYearEntity) => fiscalYear.Number !== this.props.storage.data.entity.Number);
    };

    setDefaultInterval = async (): Promise<void> => {
        // force reload FYs in case there were some change in the meantime, so we have actual data
        await refreshFiscalYears(this.props.storage.context);
        // get last active fiscal year, check every other after it
        const fiscalYears = this.getFiscalYears();
        if (fiscalYears?.length) {
            let newStartDate = undefined;
            let newEndDate = undefined;
            let i = fiscalYears.findIndex((year: IEntity) => year.StatusCode === FiscalYearStatusCode.Active);
            for (i; i < fiscalYears.length; i++) {
                const end = getUtcDayjs(fiscalYears[i].DateEnd);
                if (i === (fiscalYears.length - 1)) {
                    newStartDate = end.add(1, "day");
                    newEndDate = end.add(12, "month").endOf("month"); // end of month because of leap years;
                } else {
                    const start = getUtcDayjs(fiscalYears[i + 1]?.DateStart);
                    const monthDiff = end.diff(start, "month");
                    if (monthDiff < 0) {
                        newStartDate = end.add(1, "day");
                        newEndDate = start.subtract(1, "day");
                        if (newEndDate.diff(newStartDate, "month") > 11) {
                            newEndDate = newEndDate = end.add(12, "month").endOf("month"); // end of month because of leap years
                        }
                        break;
                    }
                }
            }
            this.props.storage.setValueByPath(FiscalYearEntity.DateStart, newStartDate.toDate());
            this.props.storage.setValueByPath(FiscalYearEntity.DateEnd, newEndDate.toDate());
            const newFiscalYearNumber = getFiscalYearNumber(newStartDate.toDate(), newEndDate.toDate(), this.getOtherFiscalYears());
            this.props.storage.setValueByPath(FiscalYearEntity.Number, newFiscalYearNumber);
            this.props.storage.refreshGroupByKey("basic");
        }
    };

    createDefaultPeriods = (defaultPeriod: FiscalYearDefaultPeriod) => {
        const fiscalYear = this.props.storage.data.entity;
        const periods: IEntity[] = createDefaultPeriods({
            dateStart: fiscalYear.DateStart,
            dateEnd: fiscalYear.DateEnd,
            includeIds: true,
            step: defaultPeriod === FiscalYearDefaultPeriod.Quarterly ? 3 : 1,
            fiscalYearNumber: fiscalYear.Number
        });

        const bindingContext = this.props.storage.data.bindingContext;
        this.props.storage.setValue(bindingContext.navigate(FiscalYearEntity.Periods), periods);
    };

    /**
     * Tests if the current data of FiscalYear looks like data generated by the createDefaultPeriods
     * We don't store default periods state on backend, but we need the switch to be initialized in its correct state
     */
    getCurrentDataLook = () => {
        if (this.hasDefaultLook()) {
            return FiscalYearDefaultPeriod.Monthly;
        } else if (this.hasDefaultLook(true)) {
            return FiscalYearDefaultPeriod.Quarterly;
        } else {
            return FiscalYearDefaultPeriod.Custom;
        }
    };

    hasDefaultLook = (quarterly?: boolean) => {
        const { DateStart, DateEnd, Periods } = this.props.storage.data.entity;

        const defaultPeriods: IEntity[] = createDefaultPeriods({
            dateStart: DateStart,
            dateEnd: DateEnd,
            step: quarterly ? 3 : 1,
            fiscalYearNumber: this.entity.Number
        });

        if (defaultPeriods?.length !== Periods?.length) {
            return false;
        }

        for (const p of Periods) {
            const defaultPeriod = defaultPeriods.find((dp: IEntity) => {
                return isSameDay(dp.DateStart, p.DateStart) && isSameDay(dp.DateEnd, p.DateEnd) && dp.Name === p.Name;
            });
            if (!defaultPeriod) {
                return false;
            }
        }


        return true;
    };

    getDefaultPeriods = () => {
        return this.props.storage.getValue(this.props.storage.data.bindingContext.navigate(DefaultPeriodsPath));
    };

    setFieldError = (bindingContext: BindingContext, message: string) => {
        const error: ISmartFieldValidationError = {
            bindingContext: bindingContext,
            data: null
        };
        if (message) {
            error.data = {
                errorType: ValidationErrorType.Field,
                message: message
            };
        }
        this.props.storage.handleValidationError(error);
    };

    clearPeriodsErrors = () => {
        const bindingContext = this.props.storage.data.bindingContext;

        for (const element of bindingContext.iterateNavigation(FiscalYearEntity.Periods, this.props.storage.data.entity.Periods)) {
            for (const path of [FiscalPeriodEntity.DateStart, FiscalPeriodEntity.DateEnd, FiscalPeriodEntity.Name]) {
                const pathBc = element.bindingContext.navigate(path);

                this.setFieldError(pathBc, null);
            }
        }
    };

    revalidatePeriods = (ignoreErrors?: boolean) => {
        const bindingContext = this.props.storage.data.bindingContext;
        let firstPeriodStartBc, monthsValid: boolean;
        let lastPeriodEndBc: BindingContext = undefined;
        for (const element of bindingContext.iterateNavigation(FiscalYearEntity.Periods, this.props.storage.data.entity.Periods)) {
            const start = element.bindingContext.navigate(FiscalYearEntity.DateStart);
            if (!firstPeriodStartBc) {
                firstPeriodStartBc = start;
            }
            if (lastPeriodEndBc) {
                const lastDate = this.props.storage.getValue(lastPeriodEndBc) as Date;
                const startDate = this.props.storage.getValue(start) as Date;
                const nextMonth = getUtcDayjs(lastDate).add(1, "month").toDate();

                monthsValid = isSameMonth(startDate, nextMonth);

                if (!ignoreErrors) {
                    if (monthsValid) {
                        this.setFieldError(lastPeriodEndBc, null);
                        this.setFieldError(start, null);
                    } else {
                        this.setFieldError(lastPeriodEndBc, this.props.storage.t("FiscalYear:Errors.NextPeriodDoesNotFollow"));
                        this.setFieldError(start, this.props.storage.t("FiscalYear:Errors.LastPeriodDoesNotFollow"));
                    }
                }
            }
            lastPeriodEndBc = element.bindingContext.navigate(FiscalPeriodEntity.DateEnd);
        }

        const periodStart = bindingContext.navigate(FiscalYearEntity.DateStart);
        const startDate = this.props.storage.getValue(periodStart) as Date;
        const firstDate = firstPeriodStartBc ? this.props.storage.getValue(firstPeriodStartBc) as Date : null;

        monthsValid = isSameMonth(startDate, firstDate);

        if (!ignoreErrors) {
            if (!firstPeriodStartBc || monthsValid) {
                this.setFieldError(periodStart, null);

                if (firstPeriodStartBc) {
                    this.setFieldError(firstPeriodStartBc, null);
                }
            } else {
                this.setFieldError(firstPeriodStartBc, this.props.storage.t("FiscalYear:Errors.FirstPeriodError"));
            }
        }


        const periodEnd = bindingContext.navigate("DateEnd");
        const endDate = this.props.storage.getValue(periodEnd) as Date;
        const lastDate = lastPeriodEndBc ? this.props.storage.getValue(lastPeriodEndBc) as Date : null;

        monthsValid = isSameMonth(endDate, lastDate);

        if (!ignoreErrors) {
            if (!lastPeriodEndBc || monthsValid) {
                this.setFieldError(periodEnd, null);

                if (lastPeriodEndBc) {
                    this.setFieldError(lastPeriodEndBc, null);
                }
            } else {
                // this.setFieldError(periodEnd, this.props.storage.t("FiscalYear:Errors.LastPeriodError"));
                this.setFieldError(lastPeriodEndBc, this.props.storage.t("FiscalYear:Errors.LastPeriodError"));
            }
        }
    };

    handleDefaultPeriodsChange = (e: ISmartFieldChange) => {
        if (e.bindingContext.getNavigationPath() === DefaultPeriodsPath) {
            const currentDefaultPeriods = this.getDefaultPeriods();

            if (e.value === currentDefaultPeriods) {
                return;
            }

            switch (e.value) {
                case FiscalYearDefaultPeriod.Monthly:
                    this.createDefaultPeriods(FiscalYearDefaultPeriod.Monthly);
                    break;
                case FiscalYearDefaultPeriod.Quarterly:
                    this.createDefaultPeriods(FiscalYearDefaultPeriod.Quarterly);
                    break;
                case FiscalYearDefaultPeriod.Custom:
                    this.createDefaultPeriods(FiscalYearDefaultPeriod.Monthly);
                    break;
            }

            this.clearPeriodsErrors();
            this.revalidatePeriods(true);
            this.props.storage.refresh();
        }
    };

    handleEndDateValues = (e: ISmartFieldChange) => {
        if (e.bindingContext.getPath() === "DateEnd" && e.value) {
            //End dates must be the end of given month
            e.value = getUtcDayjs(e.value as Date).endOf("month").toDate();
        }
    };

    handleFiscalYearEndDateChange = (e: ISmartFieldChange) => {
        if (e.bindingContext.getNavigationPath(true) === "DateEnd") {
            const { storage } = this.props;
            const { entity } = storage.data;

            const newFiscalYearNumber = getFiscalYearNumber(entity.DateStart, e.value as Date, this.getOtherFiscalYears());
            storage.setValueByPath(FiscalYearEntity.Number, newFiscalYearNumber);
            this.correctPeriodNumbers(storage);
        }
    };

    cancelDefaultPeriodsIfCriticalChange = (e: ISmartFieldChange) => {
        const criticalProps = ["Periods/Name", "Periods/DateStart", "Periods/DateEnd"];
        const path = e.bindingContext.getNavigationPath(true);

        if (criticalProps.includes(path)) {
            this.setDefaultPeriods(FiscalYearDefaultPeriod.Custom);
            this.props.storage.refreshFields();
        }
    };

    handleFiscalYearDateChange = (e: ISmartFieldChange) => {
        const path = e.bindingContext.getPath(true);

        if (path === "DateStart" || path === "DateEnd") {
            const parentContext = e.bindingContext.getParent();
            const dateStartBc = parentContext.navigate("DateStart");
            const dateStart = this.props.storage.getValue(dateStartBc);
            const dateStartError = this.props.storage.getError(dateStartBc);
            const dateStartErrorOk = dateStartError?.errorType !== ValidationErrorType.TypeError;
            const dateEndBc = parentContext.navigate("DateEnd");
            const dateEnd = this.props.storage.getValue(dateEndBc);
            const dateEndError = this.props.storage.getError(dateEndBc);
            const dateEndErrorOk = dateEndError?.errorType !== ValidationErrorType.TypeError;
            const datesValid = isValidDate(dateStart) && isValidDate(dateEnd);

            if (datesValid && dateStart >= dateEnd) {
                this.setFieldError(dateStartBc, this.props.storage.t("FiscalYear:Errors.StartDateTooBig"));
            } else if (datesValid && getUtcDayjs(dateEnd).diff(dateStart, "month") > 24) {
                this.setFieldError(dateEndBc, this.props.storage.t("FiscalYear:Errors.FiscalYearRangeTooBig"));
            } else {
                if (dateStartErrorOk) {
                    this.setFieldError(dateStartBc, null);
                }

                if (dateEndErrorOk) {
                    this.setFieldError(dateEndBc, null);
                }

                if (dateStartErrorOk && dateEndErrorOk) {
                    const defaultPeriods = this.getDefaultPeriods();
                    if (defaultPeriods !== FiscalYearDefaultPeriod.Custom) {
                        // clear periods additional data as they will be recreated
                        this.props.storage.clearCollectionAdditionalFieldDataByPath(this.props.storage.data.bindingContext.navigate(FiscalYearEntity.Periods).getFullPath());

                        this.createDefaultPeriods(defaultPeriods);
                        this.props.storage.refresh();
                    }
                }
            }
            this.revalidatePeriods(true);
        }
    };

    handleChange = async (e: ISmartFieldChange) => {
        // handlers BEFORE handleChange is called
        this.handleEndDateValues(e);
        this.cancelDefaultPeriodsIfCriticalChange(e);
        this.handleDefaultPeriodsChange(e);
        // so far, isn't supposed to be used
        // this.handlePeriodStartDateChange(e);
        this.handleFiscalYearEndDateChange(e);

        this.props.storage.handleChange(e);

        // handlers AFTER handleChange is called that need already updated data
        this.handleFiscalYearDateChange(e);

        this.props.storage.refreshFields();
    };

    handleRemove = (bc: BindingContext) => {
        this.props.storage.handleRemove(bc);
        this.revalidatePeriods(true);
        this.props.storage.refresh();
    };

    setDefaultPeriods = (defaultPeriods: FiscalYearDefaultPeriod) => {
        this.props.storage.setValue(this.props.storage.data.bindingContext.navigate(DefaultPeriodsPath), defaultPeriods);
    };

    getFiscalYearStatus = () => {
        return this.props.storage.data?.entity?.StatusCode;
    };

    onBeforeSave(): IFiscalYearEntity {
        const entity = this.entity;
        if (this.props.storage.getValueByPath(DefaultPeriodsPath) === FiscalYearDefaultPeriod.Custom) {
            entity.Periods = entity.Periods?.map((period: IEntity, idx: number) => {
                period.Number = getFiscalPeriodNumber(this.entity.Number, idx);
                return period;
            });
        }

        return entity;
    }

    save = async (args: ISaveArgs = {}) => {
        const fnOnFail = () => {
            this.props.onSaveFail?.();
            this.forceUpdate(this.scrollPageUp);
        };

        const data = args.data ?? this.onBeforeSave();
        this.props.onBeforeSave?.();

        this.revalidatePeriods();

        if (await this.props.storage.validate()) {
            fnOnFail();
            return null;
        }

        let result: IFormStorageSaveResult;
        const isNew = this.props.storage.data.bindingContext.isNew();

        if (isNew) {
            // inject current company to new fiscal year
            this.props.storage.data.entity.Company = { Id: this.context.getCompany().Id };

            const name = this.props.storage.getValueByPath("Number");
            let newChartOfAccounts: IChartOfAccountsEntity;

            try {
                const activeFiscalYears = getActiveFiscalYears(this.context, Sort.Desc);
                const newestActiveFiscalYear = activeFiscalYears?.[0];

                if (newestActiveFiscalYear) {
                    newChartOfAccounts = await createChartOfAccountsFromChartOfAccounts(
                            newestActiveFiscalYear.ChartOfAccounts.Id.toString(),
                            name,
                            this.props.storage.data.entity,
                            this.props.storage.oData);

                    result = {
                        bindingContext: this.props.storage.data.bindingContext.removeKey().addKey(newChartOfAccounts.Id.toString()),
                        data: newChartOfAccounts,
                        response: newChartOfAccounts
                    };
                }
            } catch (e) {
                this.props.storage.handleOdataError({
                    error: e,
                    bindingContext: this.props.storage.data.bindingContext
                });
                fnOnFail();
                return result;
            }

            this.props.storage.store({
                bindingContext: this.props.storage.data.bindingContext.removeKey().addKey(newChartOfAccounts.FiscalYear.Id)
            });

            this.props.storage.displaySaveOkMessage(this.props.storage.t("FiscalYear:Validation.Saved"));
            // todo better solution
            this.props.storage._preserveAlert = true;
        } else {
            result = await this.props.storage.save({
                data,
                successSubtitle: this.props.storage.t("FiscalYear:Validation.Saved")
            });

            if (!result) {
                fnOnFail();
                return result;
            }
        }

        // clear cached fiscal years to retrieve new/updated data
        refreshFiscalYears(this.props.storage.context);

        // refreshWholeTable because form doesn't change only one entity, but fiscal year and all of its periods
        this.onAfterSave(isNew, false);
        this.forceUpdate(isNew ? this.scrollPageDown : undefined);

        return result;
    };

    async setFYStatus(StatusCode: FiscalYearStatusCode) {
        this.props.storage.setBusy(true);

        await this.save({ data: { StatusCode } });
    }

    async handleDelete(): Promise<void> {
        await super.handleDelete();
        await refreshFiscalYears(this.props.storage.context);
    }

    renderButtons = () => {
        const fiscalYearStatus = this.getFiscalYearStatus();
        return (
                <ButtonGroup wrap={"wrap"}>
                    <Button onClick={this.props.onCancel}
                            isDisabled={this.shouldDisableCancelButton()}
                            isTransparent>{this.props.storage.t("Common:General.Cancel")}</Button>
                    {this.isDeletable &&
                            <SmartFormDeleteButton storage={this.props.storage}
                                                   onClick={this.handleDelete}/>
                    }
                    {!this.props.storage.data.bindingContext.isNew() &&
                            <>
                                {fiscalYearStatus === FiscalYearStatusCode.NotUsed &&
                                        <Button
                                                isTransparent
                                                isDisabled={this.shouldDisableSaveButton()}
                                                onClick={this.setFYStatus.bind(this, FiscalYearStatusCode.Active)}>{this.props.storage.t("Common:General.Activate")}</Button>
                                }
                                {fiscalYearStatus === FiscalYearStatusCode.Active &&
                                        <Button
                                                isTransparent
                                                isDisabled={this.shouldDisableSaveButton()}
                                                onClick={this.setFYStatus.bind(this, FiscalYearStatusCode.NotUsed)}>{this.props.storage.t("Common:General.Deactivate")}</Button>
                                }
                            </>
                    }
                    {fiscalYearStatus !== FiscalYearStatusCode.Closed &&
                        <Button
                            onClick={this.handleSaveClick}
                            isDisabled={this.shouldDisableSaveButton()}
                        >{this.props.storage.t("Common:General.Save")}</Button>
                    }
                </ButtonGroup>
        );
    };
}

export default withPermissionContext(FiscalYearFormView);